// uiarc: tabs (https://uiarc.dev/r/tabs.json) — Base UI + Tailwind'e çevrildi.
// - Seçili yüzey (`Tabs.Indicator`) sekmeler arasında yayla süzülür, etiketlerin altından geçer.
// - Liste taşarsa yatay kayar, kenarlarda solar, ok düğmeleri çıkar; seçili sekme görünüme kaydırılır.
// - Panel yeni sekmenin yönüne 8px kayarak gelir, eskisi 6px öbür yöne giderek solar (Base UI `data-starting-style`/
//   `data-ending-style`); çıkan panel akıştan çıkıp olduğu yerde solar, gelen panelin yüksekliği eskisinden yayla
//   kendi yüksekliğine açılır (altındaki içerik zıplamaz).
// `variant="line"` uiarc'ta yok; mevcut kullanım için alt çizgili düz görünüm olarak korundu.
import * as React from 'react'
import { Tabs as TabsPrimitive } from '@base-ui/react/tabs'
import { animate, useReducedMotion } from 'motion/react'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import { motionTokens } from '@/lib/motion'
import { messages } from '@/lib/messages'
import { cn } from '@/lib/utils'

/** Görünür panelin yüksekliği: sonraki panel buradan kendi yüksekliğine açılır. */
const TabsContext = React.createContext<{ panelHeightRef: React.RefObject<number | null> }>({
  panelHeightRef: { current: null },
})

function Tabs({
  className,
  orientation = 'horizontal',
  onValueChange,
  ...props
}: TabsPrimitive.Root.Props) {
  const root = React.useRef<HTMLDivElement>(null)
  const panelHeight = React.useRef<number | null>(null)
  const context = React.useMemo(() => ({ panelHeightRef: panelHeight }), [])
  return (
    <TabsContext.Provider value={context}>
      <TabsPrimitive.Root
        ref={root}
        data-slot="tabs"
        data-orientation={orientation}
        className={cn('group/tabs relative flex gap-4 data-horizontal:flex-col', className)}
        onValueChange={(value, details) => {
          // Çıkan panel akıştan çıkıp olduğu yere sabitlenir (kök boyut değiştirse de yerinde solar).
          const frame = root.current
          const panel = frame?.querySelector<HTMLElement>(
            ':scope > [role="tabpanel"]:not([hidden])',
          )
          if (frame && panel) {
            const from = frame.getBoundingClientRect()
            const rect = panel.getBoundingClientRect()
            frame.style.setProperty('--leave-top', `${rect.top - from.top}px`)
            frame.style.setProperty('--leave-left', `${rect.left - from.left}px`)
            frame.style.setProperty('--leave-width', `${rect.width}px`)
          }
          onValueChange?.(value, details)
        }}
        {...props}
      />
    </TabsContext.Provider>
  )
}

/** Liste taşma durumu: kenarlarda solma ve ok düğmeleri. */
function useListEdges() {
  const shell = React.useRef<HTMLDivElement>(null)
  const viewport = React.useRef<HTMLDivElement>(null)
  const list = React.useRef<HTMLDivElement>(null)
  const [edges, setEdges] = React.useState({ overflow: false, left: false, right: false })
  const update = React.useCallback(() => {
    const frame = shell.current
    const scroll = viewport.current
    if (!frame || !scroll) return
    const max = Math.max(0, scroll.scrollWidth - scroll.clientWidth)
    const next = {
      overflow: scroll.scrollWidth > frame.clientWidth + 1,
      left: scroll.scrollLeft > 1,
      right: scroll.scrollLeft < max - 1,
    }
    setEdges((previous) =>
      previous.overflow === next.overflow &&
      previous.left === next.left &&
      previous.right === next.right
        ? previous
        : next,
    )
  }, [])
  React.useLayoutEffect(() => {
    const frame = shell.current
    const scroll = viewport.current
    const content = list.current
    if (!frame || !scroll || !content) return
    const observer = new ResizeObserver(update)
    observer.observe(frame)
    observer.observe(scroll)
    observer.observe(content)
    scroll.addEventListener('scroll', update, { passive: true })
    update()
    return () => {
      observer.disconnect()
      scroll.removeEventListener('scroll', update)
    }
  }, [update])
  return { shell, viewport, list, edges }
}

function TabsList({
  className,
  variant = 'default',
  children,
  ...props
}: TabsPrimitive.List.Props & { variant?: 'default' | 'line' }) {
  const reduced = useReducedMotion()
  const { shell, viewport, list, edges } = useListEdges()
  const behavior = reduced ? 'instant' : 'smooth'

  const reveal = React.useCallback(
    (tab: HTMLElement | null) => {
      const scroll = viewport.current
      if (!scroll || !tab) return
      const frame = scroll.getBoundingClientRect()
      const item = tab.getBoundingClientRect()
      const max = Math.max(0, scroll.scrollWidth - scroll.clientWidth)
      const left = frame.left + (scroll.scrollLeft > 1 ? 34 : 0)
      const right = frame.right - (scroll.scrollLeft < max - 1 ? 34 : 0)
      const delta =
        item.left < left ? item.left - left : item.right > right ? item.right - right : 0
      if (delta) scroll.scrollBy({ left: delta, behavior })
    },
    [viewport, behavior],
  )
  // Seçili sekme (tıklama, klavye ya da dışarıdan değişim) görünüme kaydırılır.
  React.useLayoutEffect(() => {
    const node = list.current
    if (!node) return
    const reveal_ = () => reveal(node.querySelector<HTMLElement>('[role="tab"][data-active]'))
    const observer = new MutationObserver(reveal_)
    observer.observe(node, { attributes: true, attributeFilter: ['data-active'], subtree: true })
    reveal_()
    return () => observer.disconnect()
  }, [list, reveal])

  const scrollTabs = (direction: number) =>
    viewport.current?.scrollBy({
      left: direction * (viewport.current?.clientWidth ?? 0) * 0.75,
      behavior,
    })

  const line = variant === 'line'
  const arrow = cn(
    'text-foreground absolute inset-y-0 z-20 grid w-[33px] place-items-center border-0 disabled:pointer-events-none disabled:opacity-0 focus-visible:[--focus-outline-offset:-3px]',
    line ? 'bg-background' : 'bg-surface-muted',
  )
  const maskLeft = 'mask-[linear-gradient(to_right,transparent,black_35px,black)]'
  const maskRight = 'mask-[linear-gradient(to_right,black,black_calc(100%-35px),transparent)]'
  const maskBoth =
    'mask-[linear-gradient(to_right,transparent,black_35px,black_calc(100%-35px),transparent)]'

  return (
    <div
      ref={shell}
      data-slot="tabs-shell"
      data-variant={variant}
      className={cn(
        'relative isolate inline-flex max-w-full min-w-0 items-center',
        line ? 'w-full' : 'rounded-control border-border bg-surface-muted border',
      )}
    >
      {edges.overflow && (
        <button
          type="button"
          className={cn(arrow, 'left-0', line ? '' : 'rounded-l-control')}
          aria-label={messages.common.scrollTabsLeft}
          disabled={!edges.left}
          onClick={() => scrollTabs(-1)}
        >
          <ChevronLeftIcon className="size-[17px]" aria-hidden="true" />
        </button>
      )}
      <div
        ref={viewport}
        className={cn(
          'min-w-0 [scrollbar-width:none] overflow-x-auto [&::-webkit-scrollbar]:hidden',
          line ? '' : 'rounded-[inherit]',
          edges.left && edges.right
            ? maskBoth
            : edges.left
              ? maskLeft
              : edges.right
                ? maskRight
                : '',
        )}
        onFocusCapture={(event) => {
          if (event.target instanceof HTMLElement && event.target.getAttribute('role') === 'tab')
            reveal(event.target)
        }}
      >
        <TabsPrimitive.List
          ref={list}
          data-slot="tabs-list"
          data-variant={variant}
          className={cn(
            'group/tabs-list relative isolate inline-flex w-max items-center gap-1 p-1 group-data-vertical/tabs:h-fit group-data-vertical/tabs:flex-col',
            line && 'gap-0 p-0',
            className,
          )}
          {...props}
        >
          <TabsPrimitive.Indicator
            data-slot="tabs-indicator"
            className={cn(
              'absolute -z-10 [transition:left_420ms_var(--ease-spring),top_420ms_var(--ease-spring),width_420ms_var(--ease-spring),height_420ms_var(--ease-spring)] motion-reduce:transition-none',
              line
                ? 'bg-brand top-[calc(var(--active-tab-top)+var(--active-tab-height)-2px)] left-(--active-tab-left) h-0.5 w-(--active-tab-width)'
                : 'border-border bg-surface shadow-resting top-(--active-tab-top) left-(--active-tab-left) h-(--active-tab-height) w-(--active-tab-width) rounded-[calc(var(--radius-control)-3px)] border',
            )}
          />
          {children}
        </TabsPrimitive.List>
      </div>
      {edges.overflow && (
        <button
          type="button"
          className={cn(arrow, 'right-0', line ? '' : 'rounded-r-control')}
          aria-label={messages.common.scrollTabsRight}
          disabled={!edges.right}
          onClick={() => scrollTabs(1)}
        >
          <ChevronRightIcon className="size-[17px]" aria-hidden="true" />
        </button>
      )}
    </div>
  )
}

function TabsTrigger({ className, ...props }: TabsPrimitive.Tab.Props) {
  return (
    <TabsPrimitive.Tab
      data-slot="tabs-trigger"
      className={cn(
        'text-text-muted relative inline-grid min-h-(--control-height-sm) min-w-22 flex-none cursor-pointer place-items-center rounded-[calc(var(--radius-control)-3px)] px-3 text-sm font-medium whitespace-nowrap [-webkit-tap-highlight-color:transparent]',
        'ease-standard transition-colors duration-(--duration-fast) motion-reduce:transition-none',
        'hover:text-foreground active:text-foreground data-active:text-brand-strong',
        'disabled:cursor-not-allowed disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:opacity-50',
        'group-data-vertical/tabs:w-full group-data-vertical/tabs:justify-start',
        'group-data-[variant=line]/tabs-list:data-active:text-foreground group-data-[variant=line]/tabs-list:min-w-0 group-data-[variant=line]/tabs-list:rounded-none group-data-[variant=line]/tabs-list:px-4',
        "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    />
  )
}

/** Gelen panelin yüksekliği eskisinden kendi yüksekliğine yayla açılır (yalnız panel görünür olduğunda çalışır). */
function PanelHeight() {
  const { panelHeightRef } = React.useContext(TabsContext)
  const reduced = useReducedMotion()
  const marker = React.useRef<HTMLSpanElement>(null)
  React.useLayoutEffect(() => {
    const node = marker.current?.parentElement
    if (!node) return
    const to = node.offsetHeight
    const from = panelHeightRef.current ?? to
    const animating = Math.abs(from - to) > 1 && !reduced
    const release = () => {
      node.style.height = ''
      node.style.overflow = ''
    }
    if (animating && to > from) node.style.overflow = 'clip'
    const controls = animating
      ? animate(
          node,
          { height: [from, to] },
          { ...motionTokens.spring.smooth, onComplete: release },
        )
      : undefined
    panelHeightRef.current = animating ? from : to
    const observer = new ResizeObserver(() => {
      if (!node.hasAttribute('data-ending-style')) panelHeightRef.current = node.offsetHeight
    })
    observer.observe(node)
    return () => {
      observer.disconnect()
      controls?.stop()
      release()
    }
  }, [panelHeightRef, reduced])
  return <span ref={marker} hidden aria-hidden="true" />
}

function TabsContent({ className, children, ...props }: TabsPrimitive.Panel.Props) {
  return (
    <TabsPrimitive.Panel
      data-slot="tabs-content"
      className={cn(
        'leading-body focus-visible:rounded-control text-sm outline-none group-data-vertical/tabs:flex-1 focus-visible:[--focus-outline-offset:3px]',
        // Giriş/çıkış: yönle 8px / 6px kayma + solma. Çıkan panel akıştan çıkar, olduğu yerde solar.
        '[transition:opacity_var(--duration-standard)_var(--ease-enter),translate_var(--duration-spring)_var(--ease-spring)]',
        'data-starting-style:opacity-0 data-[activation-direction=left]:data-starting-style:-translate-x-2 data-[activation-direction=right]:data-starting-style:translate-x-2',
        'data-ending-style:pointer-events-none data-ending-style:absolute data-ending-style:top-(--leave-top) data-ending-style:left-(--leave-left) data-ending-style:w-(--leave-width) data-ending-style:opacity-0 data-ending-style:[transition:opacity_var(--duration-instant)_var(--ease-standard),translate_var(--duration-instant)_var(--ease-standard)] data-[activation-direction=left]:data-ending-style:translate-x-1.5 data-[activation-direction=right]:data-ending-style:-translate-x-1.5',
        'motion-reduce:translate-x-0! motion-reduce:[transition:opacity_var(--duration-instant)_var(--ease-standard)]',
        className,
      )}
      {...props}
    >
      <PanelHeight />
      {children}
    </TabsPrimitive.Panel>
  )
}

export { Tabs, TabsContent, TabsList, TabsTrigger }
